import {
  Capability,
  ComputedRole,
  OrgCapability,
  SectionCapability,
  getComputedRole,
  getCourseCapabilities,
  getOrgCapabilities,
  getPageCapabilities,
  getSectionCapabilities,
  hasOrgCapability,
  hasSectionCapability,
} from "../../shared/capabilities";
import { CourseRole, SectionRole } from "../../shared/types/roles";

import {
  courseUserSelectors,
  createAppSelector,
  orgUserSelectors,
  pageSelectors,
  sectionUserSelectors,
  selectCurrentSectionId,
  selectCurrentUserId,
  userSelectors,
} from "./base";
import {
  selectCourseGroupingUsersForCurrentCourse,
  selectCurrentUserIsCourseGroupingAdminAnywhere,
} from "./course-grouping-user";
import {
  selectCourseRoleByCourseId,
  selectCourseRoleBySectionId,
  selectCourseRoleByUserId,
  selectCurrentCourseRole,
} from "./course-user";
import { selectCurrentOrgRole, selectOrgRoleByUserId } from "./org";
import { selectPageGroupForReadyUser } from "./page-groups";
import {
  selectCurrentSectionRole,
  selectEnabledSectionUsers,
  selectSectionRoleBySectionId,
  selectSectionRoleByUserId,
} from "./section-user";
import { selectIsStudentPreview } from "./view";

import type { OrgUserType, SectionUserType, UserType } from "components/server-types";
import { HomeRole } from "utils/urls";

export const selectUserIsSuperuser = createAppSelector(
  [(state, userId: string) => userId, (state) => state.users],
  (userId, users) => users[userId]?.is_superuser,
);

export const selectUserRoleByUserId = createAppSelector(
  [(state, userId: string) => userId, (state) => state.users],
  (userId, users) => users[userId]?.role ?? null,
);

export const selectCurrentUserIsSuperuser = createAppSelector(
  [selectCurrentUserId, (state) => state.users, selectIsStudentPreview],
  (userId, users, isStudentPreview) => Boolean(!isStudentPreview && users[userId!]?.is_superuser),
);

export const selectCurrentUserRole = createAppSelector(
  [selectCurrentUserId, (state) => state.users],
  (userId, users) => users[userId!]?.role ?? null,
);

// Note: using this selector is discouraged, use capability selectors instead
export const selectCurrentUserComputedRole = createAppSelector(
  [
    selectCurrentUserIsSuperuser,
    selectCurrentUserRole,
    selectCurrentSectionRole,
    selectCurrentOrgRole,
    selectCurrentCourseRole,
    selectIsStudentPreview,
  ],
  (isSuperuser, userRole, sectionRole, orgRole, courseRole, isStudentPreview): ComputedRole =>
    getComputedRole({
      isSuperuser,
      userRole,
      sectionRole,
      orgRole,
      courseRole,
      isStudentPreview,
    }),
);

export const selectCurrentUserOrgRole = createAppSelector(
  [
    selectCurrentUserIsSuperuser,
    selectCurrentUserRole,
    selectCurrentOrgRole,
    selectCurrentCourseRole,
    selectIsStudentPreview,
  ],
  (isSuperuser, userRole, orgRole, courseRole, isStudentPreview): ComputedRole =>
    getComputedRole({
      isSuperuser,
      userRole,
      orgRole,
      courseRole,
      isStudentPreview,
    }),
);

export const selectComputedRoleByUserId = createAppSelector(
  [
    selectUserIsSuperuser,
    selectUserRoleByUserId,
    selectSectionRoleByUserId,
    selectOrgRoleByUserId,
    selectCourseRoleByUserId,
  ],
  (isSuperuser, userRole, sectionRole, orgRole, courseRole): ComputedRole =>
    getComputedRole({
      isSuperuser,
      userRole,
      sectionRole,
      orgRole,
      courseRole,
    }),
);

export const selectCurrentUserComputedRoleBySectionId = createAppSelector(
  [
    selectCurrentUserIsSuperuser,
    selectCurrentUserRole,
    selectSectionRoleBySectionId,
    selectCurrentOrgRole,
    selectCourseRoleBySectionId,
  ],
  (isSuperuser, userRole, sectionRole, orgRole, courseRole): ComputedRole =>
    getComputedRole({
      isSuperuser,
      userRole,
      sectionRole,
      orgRole,
      courseRole,
    }),
);

export const selectSectionCapabilitiesBySectionId = createAppSelector(
  [selectCurrentUserComputedRoleBySectionId],
  (computedRole) => getSectionCapabilities(computedRole),
);

export const selectSectionCapabilities = createAppSelector(
  [selectCurrentUserComputedRole],
  (computedRole) => getSectionCapabilities(computedRole),
);

export const selectCourseCapabilities = createAppSelector(
  [
    selectCurrentUserIsSuperuser,
    selectCurrentUserRole,
    selectCurrentOrgRole,
    selectCurrentCourseRole,
    selectIsStudentPreview,
  ],
  (isSuperuser, userRole, orgRole, courseRole, isStudentPreview) =>
    getCourseCapabilities(
      getComputedRole({
        isSuperuser,
        userRole,
        orgRole,
        courseRole,
        isStudentPreview,
      }),
    ),
);

export const selectOrgCapabilities = createAppSelector([selectCurrentUserOrgRole], (userOrgRole) =>
  getOrgCapabilities(userOrgRole),
);

// The current user's effective role for a specific course. Mirrors the server's
// get_computed_role_in_course.
export const selectCurrentUserComputedRoleByCourseId = createAppSelector(
  [
    selectCurrentUserIsSuperuser,
    selectCurrentUserRole,
    selectCurrentOrgRole,
    selectCourseRoleByCourseId,
    selectIsStudentPreview,
  ],
  (isSuperuser, userRole, orgRole, courseRole, isStudentPreview): ComputedRole =>
    getComputedRole({
      isSuperuser,
      userRole,
      orgRole,
      courseRole,
      isStudentPreview,
    }),
);

// All course-level capabilities for the current user in a specific course.
export const selectCourseCapabilitiesByCourseId = createAppSelector(
  [selectCurrentUserComputedRoleByCourseId],
  (userCourseRole) => getCourseCapabilities(userCourseRole),
);

export const selectPageCapabilities = createAppSelector(
  [pageSelectors.selectById, selectCurrentUserComputedRole, selectPageGroupForReadyUser],
  (page, computedRole, readyPageGroup) =>
    getPageCapabilities(page, computedRole, Boolean(readyPageGroup)),
);

export const selectSectionCapabilitiesByUserId = createAppSelector(
  [selectComputedRoleByUserId],
  (computedRole) => getSectionCapabilities(computedRole),
);

export const selectIsRecordingAgent = createAppSelector(
  [selectCurrentUserComputedRole],
  (computedRole) => computedRole === ComputedRole.RECORDING_AGENT,
);

const getSectionUsersWithSectionCapability = (
  sectionUsers: SectionUserType[],
  capability: SectionCapability,
  sectionId: string | null,
) => {
  if (!sectionId) {
    return [];
  }
  return sectionUsers.filter((sectionUser) => {
    if (sectionUser.section_id !== sectionId) {
      return false;
    }
    const sectionRole = sectionUser.role;
    return hasSectionCapability(
      // sectionRole is guaranteed to be non-null here because it's a section user and it will take precedence over isSuperuser or userRole,
      // so we can safely pass null for userRole and false for isSuperuser.
      getComputedRole({
        isSuperuser: false,
        userRole: null,
        sectionRole,
      }),
      capability,
    );
  });
};

const getSectionUserIdsWithSectionCapability = (
  sectionUsers: SectionUserType[],
  capability: SectionCapability,
  sectionId: string | null,
) => getSectionUsersWithSectionCapability(sectionUsers, capability, sectionId).map(({ id }) => id);

export const selectSectionUserIdsWithSectionCapability = createAppSelector(
  [
    selectEnabledSectionUsers,
    selectCurrentSectionId,
    (state, capability: SectionCapability) => capability,
  ],
  (sectionUsers, sectionId, capability) =>
    getSectionUserIdsWithSectionCapability(sectionUsers, capability, sectionId),
);

/** Mirrors server `User.get_role_in_section` / `SectionUser.enabled()`. */
const getSectionRole = (sectionUser: SectionUserType | null, orgUser: OrgUserType | null) => {
  if (!sectionUser || sectionUser.is_disabled || orgUser?.is_disabled) {
    return null;
  }
  return sectionUser.role;
};

/** Mirrors server `User.get_role_in_course` / `CourseUser.enabled()`. */
const getCourseRole = (courseUser: { role: CourseRole } | null, orgUser: OrgUserType | null) => {
  if (!courseUser || orgUser?.is_disabled) {
    return null;
  }
  return courseUser.role;
};

/** Mirrors server `User.get_role_in_org`. */
const getOrgRole = (orgUser: OrgUserType | null) => {
  if (!orgUser || orgUser.is_disabled) {
    return null;
  }
  return orgUser.role;
};

// User role map in the current section
// This is used to determine who has what capabilities in the current section.
export const selectComputedRoleMapInCurrentSection = createAppSelector(
  [
    userSelectors.selectAll,
    sectionUserSelectors.filterForCurrentSection,
    orgUserSelectors.selectAll,
    courseUserSelectors.filterForCurrentCourse,
    selectCourseGroupingUsersForCurrentCourse,
  ],
  (users, sectionUsers, orgUsers, courseUsers, groupingUsers): Array<[UserType, ComputedRole]> => {
    // Effective roles only (UNKNOWN dropped): enabled section, course/grouping, capability-bearing
    // org, or platform-wide authority. Disabled SectionUser/OrgUser contribution matches server.

    const sectionUsersByUserId = new Map(
      sectionUsers.map((sectionUser) => [sectionUser.user_id, sectionUser]),
    );

    const orgUsersByUserId = new Map(orgUsers.map((ou) => [ou.user_id, ou]));
    const courseUsersByUserId = new Map(courseUsers.map((cu) => [cu.user_id, cu]));
    const groupingUsersByUserId = new Map(groupingUsers.map((gu) => [gu.user_id, gu]));

    return users
      .map((user) => {
        const sectionUser = sectionUsersByUserId.get(user.id) ?? null;
        const courseUser = courseUsersByUserId.get(user.id) ?? null;
        const groupingUser = groupingUsersByUserId.get(user.id) ?? null;
        const orgUser = orgUsersByUserId.get(user.id) ?? null;

        return [
          user,
          getComputedRole({
            isSuperuser: user.is_superuser,
            userRole: user.role,
            sectionRole: getSectionRole(sectionUser, orgUser),
            orgRole: getOrgRole(orgUser),
            courseRole: getCourseRole(courseUser ?? groupingUser, orgUser),
          }),
        ] as [UserType, ComputedRole];
      })
      .filter(([, role]) => role !== ComputedRole.UNKNOWN);
  },
);

// User with specific capabilities in the current section

const getUsersWithSectionCapability = (
  computedRoleMap: Array<[UserType, ComputedRole]>,
  capability: SectionCapability,
) => {
  return computedRoleMap
    .filter(([, role]) => hasSectionCapability(role, capability))
    .map(([user]) => user);
};

export const selectUsersWhoCanParticipateInSection = createAppSelector(
  [selectComputedRoleMapInCurrentSection],
  (computedRoleMap) =>
    getUsersWithSectionCapability(computedRoleMap, Capability.CAN_PARTICIPATE_IN_SECTION),
);

// User IDs with specific capabilities in the current section

const getUserIdsWithSectionCapability = (
  computedRoleMap: Array<[UserType, ComputedRole]>,
  capability: SectionCapability,
) => {
  return getUsersWithSectionCapability(computedRoleMap, capability).map((user) => user.id);
};

export const selectUserIdsWhoAreVisibleInClass = createAppSelector(
  [selectComputedRoleMapInCurrentSection],
  (computedRoleMap) =>
    getUserIdsWithSectionCapability(computedRoleMap, Capability.IS_VISIBLE_IN_CLASS),
);

export const selectUserIdsWhoCanAuthorSection = createAppSelector(
  [selectComputedRoleMapInCurrentSection],
  (computedRoleMap) =>
    getUserIdsWithSectionCapability(computedRoleMap, Capability.CAN_AUTHOR_SECTION),
);

export const selectUserIdsWhoCanMentionAllInChat = createAppSelector(
  [selectComputedRoleMapInCurrentSection],
  (computedRoleMap) =>
    getUserIdsWithSectionCapability(computedRoleMap, Capability.CAN_MENTION_ALL_IN_CHAT),
);

export const selectUserIdsWhoCanMentionAnyBreakoutGroupInChat = createAppSelector(
  [selectComputedRoleMapInCurrentSection],
  (computedRoleMap) =>
    getUserIdsWithSectionCapability(
      computedRoleMap,
      Capability.CAN_MENTION_ANY_BREAKOUT_GROUP_IN_CHAT,
    ),
);

export const selectUserIdsWhoCanCreateChatMessage = createAppSelector(
  [selectComputedRoleMapInCurrentSection],
  (computedRoleMap) =>
    getUserIdsWithSectionCapability(computedRoleMap, Capability.CAN_CREATE_CHAT_MESSAGE),
);

export const selectUserIdsWhoCanParticipateInSection = createAppSelector(
  [selectComputedRoleMapInCurrentSection, selectEnabledSectionUsers],
  (computedRoleMap, enabledSectionUsers) => {
    const studentUserIds = getUserIdsWithSectionCapability(
      computedRoleMap,
      Capability.CAN_PARTICIPATE_IN_SECTION,
    );

    // Filter out disabled section users
    return studentUserIds.filter((user_id) =>
      enabledSectionUsers.some((cu) => cu.user_id === user_id),
    );
  },
);

// User counts with specific capabilities in the current section

export const selectCountUsersWhoCanParticipateInSection = createAppSelector(
  [selectUserIdsWhoCanParticipateInSection],
  (userIds) => userIds.length,
);

// Home-role availability selectors.
//
// These are intentionally split into atomic boolean selectors so that
// `selectAvailableHomeRoles` only recomputes when the actual rolechanges.
const selectCurrentUserCanAuthorOrg = createAppSelector([selectCurrentUserOrgRole], (orgRole) =>
  hasOrgCapability(orgRole, OrgCapability.CAN_AUTHOR_ORG),
);

export const selectCurrentUserIsCourseAdminInAnyCourse = createAppSelector(
  [courseUserSelectors.filterForCurrentUser, selectCurrentUserIsCourseGroupingAdminAnywhere],
  (courseUsers, isGroupingCourseAdmin) =>
    courseUsers.some((cu) => cu.role === CourseRole.COURSE_ADMIN) || isGroupingCourseAdmin,
);

const selectCurrentUserIsInstructorInAnySection = createAppSelector(
  [sectionUserSelectors.filterForCurrentUser],
  (sectionUsers) => sectionUsers.some((su) => su.role === SectionRole.INSTRUCTOR),
);

const selectCurrentUserIsSectionAssistantInAnySection = createAppSelector(
  [sectionUserSelectors.filterForCurrentUser],
  (sectionUsers) => sectionUsers.some((su) => su.role === SectionRole.SECTION_ASSISTANT),
);

const selectCurrentUserIsStudentInAnySection = createAppSelector(
  [sectionUserSelectors.filterForCurrentUser],
  (sectionUsers) => sectionUsers.some((su) => su.role === SectionRole.STUDENT),
);

export const selectAvailableHomeRoles = createAppSelector(
  [
    selectCurrentUserCanAuthorOrg,
    selectCurrentUserIsCourseAdminInAnyCourse,
    selectCurrentUserIsInstructorInAnySection,
    selectCurrentUserIsSectionAssistantInAnySection,
    selectCurrentUserIsStudentInAnySection,
  ],
  (canAuthorOrg, isCourseAdmin, isInstructor, isSectionAssistant, isStudent): HomeRole[] =>
    [
      (canAuthorOrg || isCourseAdmin) && HomeRole.ADMIN,
      isInstructor && HomeRole.INSTRUCTOR,
      isSectionAssistant && HomeRole.SECTION_ASSISTANT,
      isStudent && HomeRole.STUDENT,
    ].filter(Boolean) as HomeRole[],
);
